import { useEffect, useState } from "react";
import { Button, Flex, Modal, Spin, message, Popconfirm } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
} from "reactflow";
import dagre from "dagre";
import "reactflow/dist/style.css";

import {
  fetchOrganization,
  putOrganization,
  postOrganization,
  deleteOrganization,
} from "../Service/organization";
import OrganizationForm from "./organizationForm";

/* 🔧 FIXED NODE SIZE */
const nodeWidth = 240;
const nodeHeight = 170;

/* --- Build nodes and edges --- */
function buildFlatFlowElements(flatList, onEdit, onDelete) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "TB", nodesep: 40, ranksep: 70 });

  const nodes = [];
  const edges = [];

  flatList.forEach((item) => {
    const id = item.uuid.toString();

    g.setNode(id, { width: nodeWidth, height: nodeHeight });

    nodes.push({
      id,
      draggable: false,
      data: {
        label: (
          <div
            style={{
              width: "100%",
              height: "100%",
              padding: 10,
              textAlign: "center",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: 14,
                marginBottom: 6,
                wordBreak: "break-word",
              }}
            >
              {item.name}
            </div>

            <div
              style={{
                fontSize: 12,
                color: "#666",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.email}
            </div>

            <div
              style={{
                fontSize: 12,
                color: "#666",
                marginBottom: 8,
              }}
            >
              {item.contactNo}
            </div>

            <Flex justify="center" gap={12}>
              <EditOutlined
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(item);
                }}
                style={{ color: "#1677ff", cursor: "pointer" }}
              />

              <Popconfirm
                title="Delete Organization"
                description="Are you sure you want to delete this organization?"
                okText="Yes"
                cancelText="No"
                okButtonProps={{ danger: true }}
                onConfirm={(e) => {
                  e?.stopPropagation();
                  onDelete(item.uuid);
                }}
                onCancel={(e) => e?.stopPropagation()}
              >
                <DeleteOutlined
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: "red", cursor: "pointer" }}
                />
              </Popconfirm>
            </Flex>
          </div>
        ),
      },
      position: { x: 0, y: 0 },
      style: {
        width: nodeWidth,
        height: nodeHeight,
        border: "1px solid #d9d9d9",
        borderRadius: 8,
        background: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      },
    });

    if (item.parent) {
      edges.push({
        id: `edge-${item.parent.uuid}-${id}`,
        source: item.parent.uuid,
        target: id,
        animated: true,
        style: { stroke: "#999" },
      });

      g.setEdge(item.parent.uuid, id);
    }
  });

  dagre.layout(g);

  const positionedNodes = nodes.map((node) => {
    const { x, y } = g.node(node.id);
    return {
      ...node,
      position: {
        x: x - nodeWidth / 2,
        y: y - nodeHeight / 2,
      },
    };
  });

  return { nodes: positionedNodes, edges };
}

/* --- MAIN COMPONENT --- */
const Organization = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await fetchOrganization();
      const flatData = res.data || [];

      const { nodes, edges } = buildFlatFlowElements(
        flatData,
        (d) => {
          setEditData(d);
          setIsModalOpen(true);
        },
        async (uuid) => {
          await deleteData(uuid);
        }
      );

      setNodes(nodes);
      setEdges(edges);
    } catch {
      messageApi.error("Failed to fetch organization data");
    } finally {
      setIsLoading(false);
    }
  };

  const saveData = async (data) => {
    setIsLoading(true);
    try {
      const resp = editData
        ? await putOrganization(editData.uuid, data)
        : await postOrganization(data);

      if (resp) {
        await fetchData();
        setIsModalOpen(false);
        setEditData(null);
        messageApi.success(
          `Organization ${editData ? "updated" : "added"} successfully`
        );
        return true;
      }
      return false;
    } catch (err) {
      messageApi.error(err.message || "Save failed");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteData = async (uuid) => {
    try {
      await deleteOrganization(uuid);
      messageApi.success("Organization deleted successfully");
      fetchData();
    } catch {
      messageApi.error("Delete failed");
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setEditData(null);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Spin spinning={isLoading}>
      {contextHolder}
      <div style={{ width: "100%", height: "70vh" }}>
        <Flex
          justify="space-between"
          align="center"
          style={{ marginBottom: 16 }}
        >
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            Organization Structure
          </div>
          <Button type="primary" onClick={() => setIsModalOpen(true)}>
            Add
          </Button>
        </Flex>

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          fitView
        >
          <Controls />
          <MiniMap />
          <Background />
        </ReactFlow>

        <Modal
          title="Organization Form"
          centered
          open={isModalOpen}
          footer={null}
          onCancel={handleCancel}
        >
          <OrganizationForm
            editData={editData}
            saveData={saveData}
            isModalOpen={isModalOpen}
          />
        </Modal>
      </div>
    </Spin>
  );
};

export default Organization;
